import { useCallback, useEffect, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { mergeMessages, replaceOptimistic } from './messageHelpers'
import { subscribeToConversationMessages } from './supportRealtime'
import { supportApi, type SendMessageInput } from './supportApi'
import {
  supportLoadErrorKey,
  supportSendErrorKey,
  type SupportLoadErrorKey,
  type SupportSendErrorKey,
} from './supportErrors'
import {
  supportChatOpenAtom,
  supportConversationIdAtom,
  supportUnreadAtom,
} from './supportUnread'
import type { SupportAttachment, SupportConversation, SupportMessage } from './types'

interface UseSupportConversation {
  conversation: SupportConversation | null
  messages: SupportMessage[]
  loading: boolean
  sending: boolean
  /**
   * ATL-2068: a `support.*` translation key, never a raw error message. The panel translates it;
   * axios's own text («Request failed with status code 400») must not reach the merchant.
   */
  error: SupportSendErrorKey | SupportLoadErrorKey | null
  /**
   * Resolves `true` once the server has the message. `false` means it was NOT sent: the
   * optimistic bubble is withdrawn and `error` says why, and the caller still owns the text and
   * attachments (the composer puts them back, so the merchant can fix them or send again).
   */
  send: (input: SendMessageInput) => Promise<boolean>
  reload: () => Promise<void>
}

const newClientId = (): string => `cli-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const optimisticMessage = (
  conversationId: string,
  input: SendMessageInput,
  clientId: string
): SupportMessage => ({
  id: clientId,
  conversationId,
  authorType: 'customer',
  authorId: null,
  body: input.body,
  createdAt: new Date().toISOString(),
  clientId,
  status: 'sending',
  // local previews so the bubble can render the picked images/files before upload completes
  attachments: (input.attachments ?? []).map(
    (a, i): SupportAttachment => ({
      id: `${clientId}-${i}`,
      filename: a.name,
      contentType: a.type,
      size: a.size ?? 0,
      url: a.uri,
    })
  ),
})

export const useSupportConversation = (): UseSupportConversation => {
  const [conversation, setConversation] = useState<SupportConversation | null>(null)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<SupportSendErrorKey | SupportLoadErrorKey | null>(null)
  const convIdRef = useRef<string | null>(null)

  // DR-2 unread wiring
  const setUnreadConvId = useSetAtom(supportConversationIdAtom)
  const setUnread = useSetAtom(supportUnreadAtom)
  const setChatOpen = useSetAtom(supportChatOpenAtom)

  // Panel mounted ⇒ chat is open; on unmount close (unread watcher will re-poll).
  useEffect(() => {
    setChatOpen(true)
    return () => {
      setChatOpen(false)
    }
  }, [setChatOpen])

  const refetch = useCallback(async (conversationId: string) => {
    const fresh = await supportApi.getMessages(conversationId)
    setMessages((prev) => mergeMessages(prev, fresh))
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const conv = await supportApi.getOrCreateConversation()
      setConversation(conv)
      convIdRef.current = conv.id
      // DR-2: remember the conversation for the unread watcher, clear local badge, mark read.
      setUnreadConvId(conv.id)
      setUnread(0)
      void supportApi.markRead(conv.id).catch(() => {})
      await refetch(conv.id)
    } catch (e) {
      setError(supportLoadErrorKey(e))
    } finally {
      setLoading(false)
    }
  }, [refetch, setUnreadConvId, setUnread])

  useEffect(() => {
    let unsubscribe = (): void => {}
    let cancelled = false
    void (async () => {
      await reload()
      const id = convIdRef.current
      if (!cancelled && id) {
        unsubscribe = subscribeToConversationMessages(id, () => {
          void refetch(id)
          void supportApi.markRead(id).catch(() => {})
        })
      }
    })()
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [reload, refetch])

  const send = useCallback(async (input: SendMessageInput): Promise<boolean> => {
    const id = convIdRef.current
    // No conversation means loading it failed, and that error is already on screen.
    if (!id) return false
    const clientId = newClientId()
    setSending(true)
    setError(null)
    // optimistic: show it immediately as 'sending'
    setMessages((prev) => mergeMessages(prev, [optimisticMessage(id, input, clientId)]))
    try {
      const created = await supportApi.sendMessage(id, { ...input, clientId })
      setMessages((prev) => replaceOptimistic(prev, clientId, created))
      return true
    } catch (e) {
      // ATL-2068: the message goes back to the composer, so the bubble is withdrawn. Keeping it
      // as a «retry» bubble as well would offer two ways to send the same message, and using
      // both would post it twice. Only the optimistic copy goes: if a realtime refetch already
      // brought the server's own row for this clientId, the server has it and it stays visible.
      setMessages((prev) => prev.filter((m) => !(m.clientId === clientId && m.status === 'sending')))
      setError(supportSendErrorKey(e))
      return false
    } finally {
      setSending(false)
    }
  }, [])

  return { conversation, messages, loading, sending, error, send, reload }
}
